import { useEffect, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Button,
  MenuItem,
  Divider,
  IconButton,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import CloseIcon from "@mui/icons-material/Close";
import { AddCircleOutline, Public } from "@mui/icons-material";
import IOSSwitch from "../../../components/IOSSwitch";
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";

// Regular expression for IP address and URL validation
const ipRegex =
  /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9\-]+\.)+[a-zA-Z]{2,6}(\/[^\s]*)?$/;

interface CustomAssetDrawerProps {
  open: boolean;
  onClose: () => void;
  onUpload: (values: any) => void;
  assetsCredData: any[];
  onPasswordTypeChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  agentList?: any[];
  defaultAgentId?: string;
}

const CustomAssetDrawer = ({
  open,
  onClose,
  onUpload,
  assetsCredData,
  onPasswordTypeChange,
  agentList,
  defaultAgentId,
}: CustomAssetDrawerProps) => {
  const [agents, setAgents] = useState<any[]>(agentList || []);
  const [loadingAgents, setLoadingAgents] = useState<boolean>(false);

  useEffect(() => {
    if (agentList && agentList.length > 0) {
      setAgents(agentList);
    }
  }, [agentList]);

  useEffect(() => {
    if (open) {
      const fetchAgents = async () => {
        if (agents.length === 0) setLoadingAgents(true);
        try {
          const res = await axiosInstance.get(END_POINTS.CLOUD_AGENT);
          const data = res?.data?.data || res?.data || [];
          const list = Array.isArray(data) ? data : data.agent_list || [];
          setAgents(list);
        } catch (err) {
          console.error("Error fetching agents in CustomAssetDrawer:", err);
        } finally {
          setLoadingAgents(false);
        }
      };
      fetchAgents();
    }
  }, [open]);

  const activeAgents = agents.filter(
    (agent: any) => String(agent?.status_display || "").trim().toLowerCase() === "active"
  );
  const displayAgents = activeAgents;

  const formik = useFormik({
    initialValues: {
      customPort: "false",
      customPortNumber: "",
      assetIp: "",
      credentialType: "ssh",
      agentId: "",
      credentialSelect: "",
      username: "",
      description: "",
      credName: "",
      passwordType: "password",
      password: "",
      execPassword: false,
      autoAssign: false,
      execPasswordText: "",
      saveCredential: false,
      apiToken: "",
      apiPasswordType: "api_token",
      apiUsername: "",
      apiPassword: "",
      apiExecPassword: false,
      apiAutoAssign: false,
      apiExecPasswordText: "",
    },
    validationSchema: Yup.object({
      agentId: Yup.string().required("Please select an Agent"),
      assetIp: Yup.string()
        .required("Asset IP is required")
        .test("ip-or-url", "Invalid IP address or URL format", function (value) {
          if (!value) return false;
          return ipRegex.test(value) || urlRegex.test(value);
        }),
      customPortNumber: Yup.string().when("customPort", {
        is: "true",
        then: (schema) =>
          schema
            .required("Custom Port Number required")
            .matches(/^\d+$/, "Only numbers are allowed")
            .test("port-range", "Port must be between 1 and 65535", function (value) {
              if (!value) return false;
              const port = parseInt(value);
              return port >= 1 && port <= 65535;
            }),
        otherwise: (schema) => schema.notRequired(),
      }),
      apiToken: Yup.string().when(["credentialType", "credentialSelect"], {
        is: (type: any, select: any) =>
          type === "api" && select === "Create New Credential",
        then: (schema) => schema.required("API Token required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      apiExecPasswordText: Yup.string().when(["apiExecPassword", "apiAutoAssign"], {
        is: (exec: any, auto: any) => exec && !auto,
        then: (schema) => schema.required("Exec Password required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      credentialSelect: Yup.string().required("Please select a credential"),
      username: Yup.string().when(["credentialType", "credentialSelect"], {
        is: (type: any, select: any) =>
          (type === "ssh" || type === "windows") && select === "Create New Credential",
        then: (schema) => schema.required("Username required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      credName: Yup.string().when(["credentialType", "credentialSelect"], {
        is: (type: any, select: any) =>
          (type === "ssh" || type === "api" || type === "windows") &&
          select === "Create New Credential",
        then: (schema) => schema.required("Credential Name required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      password: Yup.string().when(["credentialType", "credentialSelect", "passwordType"], {
        is: (type: any, select: any, ptype: any) =>
          ((type === "ssh" && ptype === "password") || type === "windows") &&
          select === "Create New Credential",
        then: (schema) => schema.required("Password required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      execPasswordText: Yup.string().when(["execPassword", "autoAssign"], {
        is: (exec: any, auto: any) => exec && !auto,
        then: (schema) => schema.required("Exec Password required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    validate: (values) => {
      const errors: any = {};

      // For SSH credentials, require either execPassword or autoAssign when creating new credential with password type
      if (
        values.credentialType === "ssh" &&
        values.credentialSelect === "Create New Credential" &&
        values.passwordType === "password"
      ) {
        if (!values.execPassword && !values.autoAssign) {
          errors.execPassword = "Either Exec Password or Auto Assign must be selected";
        }
      }

      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      console.log("Form Values:", values);
      onUpload(values);
      resetForm();
    },
  });

  useEffect(() => {
    if (!open || loadingAgents || formik.values.agentId || displayAgents.length === 0) return;

    const preferred = displayAgents.find((a: any) => a.id === defaultAgentId);
    const pick = preferred ?? displayAgents[0];

    formik.setFieldValue("agentId", pick.id, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loadingAgents, displayAgents.length, defaultAgentId]);

  // Reset custom port number if false
  useEffect(() => {
    if (formik.values.customPort === "false") {
      formik.setFieldValue("customPortNumber", "");
      formik.setTouched({
        customPortNumber: false,
      });
    }
  }, [formik.values.customPort]);

  // Reset credential fields if switching types
  useEffect(() => {
    formik.setFieldValue("credentialSelect", "");
    if (formik.values.credentialType === "windows") {
      formik.setFieldValue("username", "");
      formik.setFieldValue("description", "");
      formik.setFieldValue("credName", "");
      formik.setFieldValue("passwordType", "password");
      formik.setFieldValue("password", "");
      formik.setFieldValue("execPassword", false);
      formik.setFieldValue("autoAssign", true);
      formik.setFieldValue("execPasswordText", "");
      formik.setFieldValue("apiToken", "");
      formik.setFieldValue("apiUsername", "");
      formik.setFieldValue("apiPassword", "");
      formik.setTouched({
        credentialSelect: false,
        username: false,
        description: false,
        credName: false,
        password: false,
        apiToken: false,
      });
    } else if (formik.values.credentialType === "api") {
      formik.setFieldValue("username", "");
      formik.setFieldValue("description", "");
      formik.setFieldValue("credName", "");
      formik.setFieldValue("passwordType", "none");
      formik.setFieldValue("password", "");
      formik.setFieldValue("execPassword", false);
      formik.setFieldValue("autoAssign", false);
      formik.setFieldValue("execPasswordText", "");
      formik.setTouched({
        credentialSelect: false,
        username: false,
        description: false,
        credName: false,
        passwordType: false,
        password: false,
        execPassword: false,
        autoAssign: false,
        execPasswordText: false,
      });
    } else {
      formik.setFieldValue("apiToken", "");
      formik.setFieldValue("apiUsername", "");
      formik.setFieldValue("apiPasswordType", "api_token");
      formik.setFieldValue("apiPassword", "");
      formik.setFieldValue("apiExecPassword", false);
      formik.setFieldValue("apiAutoAssign", false);
      formik.setFieldValue("apiExecPasswordText", "");
      formik.setTouched({
        apiToken: false,
        apiUsername: false,
        apiPasswordType: false,
        apiPassword: false,
        apiExecPassword: false,
        apiAutoAssign: false,
        apiExecPasswordText: false,
      });
    }
  }, [formik.values.credentialType]);

  // Reset create new credential fields if not selected
  useEffect(() => {
    if (formik.values.credentialSelect !== "Create New Credential") {
      formik.setFieldValue("username", "");
      formik.setFieldValue("description", "");
      formik.setFieldValue("credName", "");
      formik.setFieldValue("passwordType", "none");
      formik.setFieldValue("password", "");
      formik.setFieldValue("execPassword", false);
      formik.setFieldValue("autoAssign", false);
      formik.setFieldValue("execPasswordText", "");
      formik.setFieldValue("saveCredential", false);

      formik.setFieldValue("apiToken", "");
      formik.setFieldValue("apiUsername", "");
      formik.setFieldValue("apiPasswordType", "api_token");
      formik.setFieldValue("apiPassword", "");
      formik.setFieldValue("apiExecPassword", false);
      formik.setFieldValue("apiAutoAssign", false);
      formik.setFieldValue("apiExecPasswordText", "");

      formik.setTouched({
        username: false,
        description: false,
        credName: false,
        passwordType: false,
        password: false,
        execPassword: false,
        autoAssign: false,
        execPasswordText: false,
        saveCredential: false,
        apiToken: false,
        apiUsername: false,
        apiPasswordType: false,
        apiPassword: false,
        apiExecPassword: false,
        apiAutoAssign: false,
        apiExecPasswordText: false,
      });
    }
  }, [formik.values.credentialSelect]);

  // Handle toggles logic
  useEffect(() => {
    if (formik.values.autoAssign && formik.values.credentialType === "ssh") {
      formik.setFieldValue("execPassword", false);
      formik.setFieldValue("execPasswordText", "");
    }
    if (!formik.values.execPassword && formik.values.credentialType === "ssh") {
      formik.setFieldValue("execPasswordText", "");
    }
    if (formik.values.execPassword && formik.values.credentialType === "ssh") {
      formik.setFieldValue("autoAssign", false);
    }
  }, [formik.values.autoAssign, formik.values.execPassword, formik.values.credentialType]);

  useEffect(() => {
    if (formik.values.credentialType === "ssh") {
      formik.setFieldValue("password", "");
      formik.setFieldValue("execPassword", false);
      formik.setFieldValue("autoAssign", false);
      formik.setFieldValue("execPasswordText", "");
      formik.setFieldValue("saveCredential", false);
    }
  }, [formik.values.passwordType]);

  const handleCancel = () => {
    formik.resetForm();
    onClose();
  };

  const isSubmitEnabled = () => {
    if (!formik.isValid || formik.isSubmitting) {
      return false;
    }

    if (
      formik.values.credentialType === "ssh" &&
      formik.values.credentialSelect === "Create New Credential" &&
      formik.values.passwordType === "password"
    ) {
      return formik.values.execPassword || formik.values.autoAssign;
    }

    return true;
  };

  const filteredCredentials = assetsCredData.filter((cred: any) => {
    if (!cred) return false;
    const cType = (cred.component || cred.credential_type || cred.type || "").toLowerCase();
    if (!cType) return true;
    if (formik.values.credentialType === "windows") {
      return cType === "windows" || cType === "winrm";
    }
    return cType === formik.values.credentialType.toLowerCase();
  });

  return (
    <Drawer anchor="right" open={open} onClose={handleCancel}>
      <Box sx={{ width: 400, p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6">Rescan Config File with SSH/API/WINDOWS</Typography>
          <IconButton
            onClick={handleCancel}
            color="secondary"
            size="small"
            sx={{ marginLeft: "10px" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Divider />
        </Box>
        <form onSubmit={formik.handleSubmit}>
          {/* Custom Port */}
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <Typography>Custom Port</Typography>
            <RadioGroup
              row
              name="customPort"
              value={formik.values.customPort}
              onChange={formik.handleChange}
            >
              <FormControlLabel value="true" control={<Radio />} label="True" />
              <FormControlLabel value="false" control={<Radio />} label="False" />
            </RadioGroup>
          </FormControl>
          <TextField
            label="Custom Port Number"
            name="customPortNumber"
            value={formik.values.customPortNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.values.customPort !== "true"}
            error={formik.touched.customPortNumber && Boolean(formik.errors.customPortNumber)}
            helperText={formik.touched.customPortNumber && formik.errors.customPortNumber}
            fullWidth
            sx={{ mb: 2 }}
            autoComplete="off"
            inputProps={{
              autoComplete: "off",
            }}
          />

          {/* Asset IP */}
          <TextField
            label="Asset IP / URL"
            name="assetIp"
            value={formik.values.assetIp}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.assetIp && Boolean(formik.errors.assetIp)}
            helperText={formik.touched.assetIp && formik.errors.assetIp}
            fullWidth
            sx={{ mb: 2 }}
            autoComplete="off"
            inputProps={{
              autoComplete: "off",
            }}
          />

          {/* Select Agent Dropdown */}
          <TextField
            select
            fullWidth
            label="Select Agent *"
            name="agentId"
            value={formik.values.agentId}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.agentId && Boolean(formik.errors.agentId)}
            helperText={
              formik.touched.agentId && typeof formik.errors.agentId === "string"
                ? formik.errors.agentId
                : ""
            }
            sx={{ mb: 2 }}
            disabled={loadingAgents}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  className: "scroll",
                  style: { maxHeight: 300 },
                  sx: {
                    maxHeight: 300,
                    "& .MuiMenuItem-root": {
                      minHeight: "48px",
                      display: "flex",
                      alignItems: "center",
                      paddingLeft: "16px",
                      paddingRight: "16px",
                    },
                  },
                },
              },
            }}
          >
            {loadingAgents ? (
              <MenuItem disabled>Loading agents...</MenuItem>
            ) : displayAgents.length === 0 ? (
              <MenuItem disabled>No active agents available</MenuItem>
            ) : (
              displayAgents.map((agent: any) => (
                <MenuItem key={agent.id} value={agent.id}>
                  {agent.name}
                </MenuItem>
              ))
            )}
          </TextField>

          {/* Credential Type */}
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <Typography>Credential Type</Typography>
            <RadioGroup
              row
              name="credentialType"
              value={formik.values.credentialType}
              onChange={formik.handleChange}
            >
              <FormControlLabel value="ssh" control={<Radio />} label="SSH" />
              <FormControlLabel value="api" control={<Radio />} label="API" />
              <FormControlLabel value="windows" control={<Radio />} label="Windows" />
            </RadioGroup>
          </FormControl>

          {/* Credentials Select */}
          <TextField
            select
            fullWidth
            label="Select Credentials *"
            name="credentialSelect"
            value={formik.values.credentialSelect}
            onChange={formik.handleChange}
            error={
              formik.touched.credentialSelect &&
              Boolean(formik.errors.credentialSelect)
            }
            helperText={
              formik.touched.credentialSelect &&
              typeof formik.errors.credentialSelect === "string"
                ? formik.errors.credentialSelect
                : ""
            }
            sx={{ mb: 2 }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  className: "scroll",
                  style: { maxHeight: 300 },
                  sx: {
                    maxHeight: 300,
                    "& .MuiMenuItem-root": {
                      minHeight: "48px",
                      display: "flex",
                      alignItems: "center",
                      paddingLeft: "16px",
                      paddingRight: "16px",
                    },
                  },
                },
              },
            }}
          >
            <MenuItem
              value="Create New Credential"
              sx={{
                display: "flex",
                alignItems: "center",
                minHeight: "48px",
                paddingLeft: "16px",
                paddingRight: "16px",
              }}
            >
              <AddCircleOutline
                sx={{
                  color: "blue",
                  marginRight: "12px",
                  fontSize: "20px",
                  verticalAlign: "middle",
                }}
              />
              <span style={{ verticalAlign: "middle", lineHeight: "20px" }}>
                Create New Credential
              </span>
            </MenuItem>

            {filteredCredentials.map((cred: any) => (
              <MenuItem
                key={cred.id}
                value={cred.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minHeight: "48px",
                  paddingLeft: "16px",
                  paddingRight: "16px",
                }}
              >
                {cred.global_cred && (
                  <Public
                    sx={{
                      color: "gray",
                      marginRight: "12px",
                      fontSize: "20px",
                      verticalAlign: "middle",
                    }}
                  />
                )}
                <span style={{ verticalAlign: "middle", lineHeight: "20px" }}>
                  {cred.name}
                </span>
              </MenuItem>
            ))}
          </TextField>

          {/* SSH Credential */}
          {formik.values.credentialType === "ssh" && (
            <>
              {formik.values.credentialSelect === "Create New Credential" && (
                <>
                  <TextField
                    label="Username"
                    name="username"
                    value={formik.values.username}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.username && Boolean(formik.errors.username)}
                    helperText={formik.touched.username && formik.errors.username}
                    fullWidth
                    sx={{ mb: 2 }}
                    autoComplete="off"
                    inputProps={{
                      autoComplete: "off",
                      "data-lpignore": "true",
                      "data-form-type": "other",
                    }}
                  />
                  <TextField
                    label="Description"
                    name="description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label="Credential Name"
                    name="credName"
                    value={formik.values.credName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.credName && Boolean(formik.errors.credName)}
                    helperText={formik.touched.credName && formik.errors.credName}
                    fullWidth
                    sx={{ mb: 2 }}
                    autoComplete="off"
                    inputProps={{
                      autoComplete: "off",
                      "data-lpignore": "true",
                      "data-form-type": "other",
                    }}
                  />

                  {/* Save Credential Checkbox */}
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2, paddingLeft: 2 }}>
                    <FormControlLabel
                      control={
                        <IOSSwitch
                          offColor="#696595!important"
                          onColor="#3a2ada!important"
                          sx={{ mr: 2 }}
                          checked={formik.values.saveCredential}
                          onChange={() =>
                            formik.setFieldValue("saveCredential", !formik.values.saveCredential)
                          }
                        />
                      }
                      label="Save Credential"
                    />
                  </Box>

                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <Typography>Password Type</Typography>
                    <RadioGroup
                      row
                      name="passwordType"
                      value={formik.values.passwordType}
                      onChange={(e) => {
                        formik.handleChange(e);
                        if (onPasswordTypeChange) {
                          onPasswordTypeChange(e);
                        }
                      }}
                    >
                      <FormControlLabel value="password" control={<Radio />} label="Password" />
                      <FormControlLabel value="private_key" control={<Radio />} label="Private Key" />
                    </RadioGroup>
                  </FormControl>
                  {/* Password Input */}
                  {formik.values.passwordType === "password" && (
                    <TextField
                      label="Password"
                      name="password"
                      type="password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.password && Boolean(formik.errors.password)}
                      helperText={formik.touched.password && formik.errors.password}
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                  )}
                  {/* Toggles */}
                  {formik.values.passwordType === "password" && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2, paddingLeft: 2 }}>
                      <FormControlLabel
                        control={
                          <IOSSwitch
                            offColor="#696595!important"
                            onColor="#3a2ada!important"
                            sx={{ mr: 2 }}
                            checked={formik.values.execPassword}
                            onChange={() =>
                              formik.setFieldValue("execPassword", !formik.values.execPassword)
                            }
                            disabled={formik.values.autoAssign}
                          />
                        }
                        label="Exec Password"
                      />
                      <FormControlLabel
                        control={
                          <IOSSwitch
                            offColor="#696595!important"
                            onColor="#3a2ada!important"
                            sx={{ mr: 2 }}
                            checked={formik.values.autoAssign}
                            onChange={() =>
                              formik.setFieldValue("autoAssign", !formik.values.autoAssign)
                            }
                            disabled={formik.values.execPassword}
                          />
                        }
                        label="Auto Assign"
                      />
                    </Box>
                  )}

                  {/* Validation Error for Exec Password or Auto Assign */}
                  {formik.values.passwordType === "password" &&
                    !formik.values.execPassword &&
                    !formik.values.autoAssign &&
                    (formik.touched.execPassword || formik.touched.autoAssign) && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ display: "block", mt: 1, mb: 2, paddingLeft: 2 }}
                      >
                        Either Exec Password or Auto Assign must be selected
                      </Typography>
                    )}

                  {/* Exec Password Field */}
                  {formik.values.execPassword && (
                    <TextField
                      label="Exec Password"
                      name="execPasswordText"
                      type="password"
                      value={formik.values.execPasswordText}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.execPasswordText && Boolean(formik.errors.execPasswordText)}
                      helperText={formik.touched.execPasswordText && formik.errors.execPasswordText}
                      fullWidth
                      sx={{ mb: 2 }}
                      autoComplete="off"
                      inputProps={{
                        autoComplete: "off",
                      }}
                    />
                  )}
                </>
              )}
            </>
          )}

          {/* Windows Credential */}
          {formik.values.credentialType === "windows" && (
            <>
              {formik.values.credentialSelect === "Create New Credential" && (
                <>
                  <TextField
                    label="Username"
                    name="username"
                    value={formik.values.username}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.username && Boolean(formik.errors.username)}
                    helperText={formik.touched.username && formik.errors.username}
                    fullWidth
                    sx={{ mb: 2 }}
                    autoComplete="off"
                    inputProps={{
                      autoComplete: "off",
                      "data-lpignore": "true",
                      "data-form-type": "other",
                    }}
                  />
                  <TextField
                    label="Password"
                    name="password"
                    type="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    helperText={formik.touched.password && formik.errors.password}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label="Description"
                    name="description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label="Credential Name"
                    name="credName"
                    value={formik.values.credName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.credName && Boolean(formik.errors.credName)}
                    helperText={formik.touched.credName && formik.errors.credName}
                    fullWidth
                    sx={{ mb: 2 }}
                    autoComplete="off"
                    inputProps={{
                      autoComplete: "off",
                      "data-lpignore": "true",
                      "data-form-type": "other",
                    }}
                  />
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2, paddingLeft: 2 }}>
                    <FormControlLabel
                      control={
                        <IOSSwitch
                          offColor="#696595!important"
                          onColor="#3a2ada!important"
                          sx={{ mr: 2 }}
                          checked={formik.values.saveCredential}
                          onChange={() =>
                            formik.setFieldValue("saveCredential", !formik.values.saveCredential)
                          }
                        />
                      }
                      label="Save Credential"
                    />
                  </Box>
                </>
              )}
            </>
          )}

          {/* API Credential */}
          {formik.values.credentialType === "api" && (
            <>
              {formik.values.credentialSelect === "Create New Credential" && (
                <>
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <Typography>Password Type</Typography>
                    <RadioGroup
                      row
                      name="apiPasswordType"
                      value={formik.values.apiPasswordType}
                      onChange={formik.handleChange}
                    >
                      <FormControlLabel value="api_token" control={<Radio />} label="API Token" />
                    </RadioGroup>
                  </FormControl>

                  <TextField
                    label="API Token"
                    name="apiToken"
                    value={formik.values.apiToken}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.apiToken && Boolean(formik.errors.apiToken)}
                    helperText={formik.touched.apiToken && formik.errors.apiToken}
                    fullWidth
                    type="text"
                    sx={{ mb: 2 }}
                    autoComplete="off"
                    inputProps={{
                      autoComplete: "off",
                    }}
                  />

                  <TextField
                    label="Credential Name"
                    name="credName"
                    value={formik.values.credName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.credName && Boolean(formik.errors.credName)}
                    helperText={formik.touched.credName && formik.errors.credName}
                    fullWidth
                    sx={{ mb: 2 }}
                    autoComplete="off"
                    inputProps={{
                      autoComplete: "off",
                    }}
                  />

                  {/* Save Credential Toggle for API */}
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography sx={{ mr: 2 }}>Save Credential</Typography>
                    <IOSSwitch
                      checked={formik.values.saveCredential}
                      onChange={(event) =>
                        formik.setFieldValue("saveCredential", event.target.checked)
                      }
                    />
                  </Box>
                </>
              )}
            </>
          )}

          {/* Buttons */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              onClick={() => formik.handleSubmit()}
              disabled={!isSubmitEnabled()}
            >
              Submit
            </Button>
          </Box>
        </form>
      </Box>
    </Drawer>
  );
};

export default CustomAssetDrawer;
